import React, { useContext, useEffect, useState } from 'react';
import DialogBox from '../../Common/Layout/DialogBox';
import {
  IContact,
  ICventContact,
  IInvoice,
  IInvoiceInput,
  IPaymentDetails,
  useContactQuery,
  useCreateInvoiceMutation,
  usePaymentDetailsQuery
} from '../../../generated/graphql';
import FormStepper from '../../Common/Layout/FormStepper';
import { Box } from '@mui/material';
import { InvoiceType } from '../../../models/invoice';
import PaymentDetailsStep from '../../Invoices/Wizard/PaymentDetailsStep';
import ClientStep from '../../Invoices/Wizard/ClientStep';
import ProductAndPricingStep from '../../Invoices/Wizard/ProductAndPricingStep';
import CancelButton from '../../Common/Buttons/CancelButton';
import SaveButton from '../../Common/Buttons/SaveButton';
import InvoiceStep from '../../Invoices/Wizard/InvoiceStep';
import Invoice from '../../Invoices/Wizard/Invoice';
import { AppContext, InvoiceProductInfo } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import { defaultPaymentDto, defaultProductInfo } from '../../../models/defaults';
import { InvoiceStatus } from '../../../enums/InvoiceStatusEnums';
import { getInvoiceProductInfoDto } from '../../Invoices/InvoiceList';
import Loader from '../../Common/Loader';
import './InvoiceStepper.scss';

interface IProps {
  onClose: () => void;
  cventContact: ICventContact;
  invoice?: InvoiceType;
  handleUpdateLocationContacts?: (update: boolean) => void;
}

const steps = ['My Details', 'Client', 'Product & Pricing', 'Invoice'];

const InvoiceStepper: React.FunctionComponent<IProps> = ({
  onClose,
  cventContact,
  invoice,
  handleUpdateLocationContacts
}: IProps) => {
  const { data } = usePaymentDetailsQuery();
  const { state, dispatch } = useContext(AppContext);
  const [activeStep, setActiveStep] = useState(1);
  const [paymentDetails, setPaymentDetails] = useState<IPaymentDetails>(
    data?.result ?? defaultPaymentDto
  );
  const [productInfo, setProductInfo] = useState<InvoiceProductInfo>(defaultProductInfo);
  const [invoiceFullScreen, setInvoiceFullScreen] = useState(false);
  const [contact, setContact] = useState<IContact | null>(null);
  const [createInvoice, createInvoiceResult] = useCreateInvoiceMutation();
  const contactQuery = useContactQuery({
    variables: {
      id: Number(cventContact.contact_id)
    }
  });

  useEffect(() => {
    const invoice = state.selectedInvoice;
    if (data && data.result) {
      setPaymentDetails(data.result);
      if (invoice) {
        invoice.paymentDetails = data.result;
      }
    }
    if (contactQuery.data && contactQuery.data.result) {
      setContact(contactQuery.data.result as IContact);
    }
  }, [data, contactQuery.data]);

  const handleInvoiceConfirm = () => {
    if (handleUpdateLocationContacts) {
      handleUpdateLocationContacts(true);
    }
  };

  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <PaymentDetailsStep
            paymentDetailsInput={paymentDetails}
            updateDetails={setPaymentDetails}
          />
        );
      case 1:
        return <ClientStep cventContact={contact} />;
      case 2:
        return (
          <ProductAndPricingStep productInfoInput={productInfo} setProductInfo={setProductInfo} />
        );
      case 3:
        return (
          <InvoiceStep
            setPreview={() => setInvoiceFullScreen(!invoiceFullScreen)}
            editInvoice={handleInvoiceConfirm}
          >
            <Invoice
              fullscreen={invoiceFullScreen}
              cventData={{ contact: contact as IContact, paymentDetails, productInfo }}
            />
          </InvoiceStep>
        );
      default:
        return <h1>unknown</h1>;
    }
  };
  const isValidInvoice = (selectedInvoice: InvoiceType) => {
    if (!selectedInvoice.productInfo) {
      return false;
    }
    if (!selectedInvoice.paymentDetails) {
      return false;
    }
    if (!selectedInvoice.company) {
      return false;
    }
    if (!selectedInvoice.contact) {
      return false;
    }
    if (!selectedInvoice.address) {
      return false;
    }
    return true;
  };

  const getInvoiceInputDto = (selectedInvoice: InvoiceType, originalInvoice?: IInvoice) => {
    const res = {
      product_id: parseInt(selectedInvoice.productInfo?.product?.id as string),
      membership_id: parseInt(contact?.company?.membership?.id as string),
      payment_details_id: parseInt(selectedInvoice.paymentDetails?.id as string),
      total: selectedInvoice.productInfo?.total as number,
      discount: selectedInvoice.productInfo?.discount ?? 0,
      vat: selectedInvoice.productInfo?.vatPercent ?? 0,
      details: getPaymentDetailsDto(selectedInvoice, originalInvoice),
      notes: selectedInvoice.notes,
      cvent_contact_id: Number(cventContact?.id)
    } as IInvoiceInput;
    return res;
  };

  const getPaymentDetailsDto = (selectedInvoice: InvoiceType, originalInvoice?: IInvoice) => {
    return {
      company_name: selectedInvoice.company?.name ?? originalInvoice?.details?.company_name,
      company_address_vat:
        selectedInvoice.company?.main_address?.complete_vat_number ??
        originalInvoice?.details?.company_address_vat,
      contact_first_name:
        selectedInvoice.contact?.first_name ?? originalInvoice?.details?.contact_first_name,
      contact_last_name:
        selectedInvoice.contact?.last_name ?? originalInvoice?.details?.contact_last_name,
      contact_email: selectedInvoice.contact?.email ?? originalInvoice?.details?.contact_email,
      address_name: selectedInvoice.address?.name ?? originalInvoice?.details?.address_name,
      street: selectedInvoice.address?.street ?? originalInvoice?.details?.street,
      city: contact?.address?.city ?? originalInvoice?.details?.city,
      zip: selectedInvoice.address?.zip ?? originalInvoice?.details?.zip,
      country: selectedInvoice.address?.country?.name ?? originalInvoice?.details?.country,
      region: contact?.address?.country?.region?.name ?? originalInvoice?.details?.region,
      title_external:
        selectedInvoice.contact?.title_external ?? originalInvoice?.details?.title_external,
      prefix: selectedInvoice?.contact?.prefix ?? originalInvoice?.details?.prefix
    };
  };

  const handleNext = () => {
    if (activeStep === 2 && productInfo.product === null) {
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: 'You should select a product'
        }
      });
    } else if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    } else if (activeStep === steps.length - 1) {
      const selectedInvoice = state.selectedInvoice as InvoiceType;
      if (selectedInvoice.status === InvoiceStatus.New) {
        if (isValidInvoice(selectedInvoice)) {
          const input = getInvoiceInputDto(selectedInvoice);
          createInvoice({
            variables: {
              input
            },
            update(_, { data }) {
              if (data?.result) {
                dispatch({
                  type: Actions.SetInvoice,
                  payload: {
                    invoice: data.result as IInvoice,
                    productInfo: getInvoiceProductInfoDto(data.result as IInvoice)
                  }
                });

                dispatch({
                  type: Actions.ShowMessage,
                  payload: {
                    severity: 'success',
                    text: `You have successfully created new invoice for contact: ${contact?.fullName}`
                  }
                });
              }
            }
          }).catch((e) => {
            dispatch({
              type: Actions.ShowMessage,
              payload: {
                severity: 'error',
                text: e.message,
                autoHide: 6000
              }
            });
          });
        }
      } else {
      }
    }
  };

  return (
    <DialogBox
      id="cvent-invoice-stepper"
      width={1200}
      onClose={onClose}
      title={invoice ? 'Create invoice' : 'Invoice'}
    >
      <FormStepper steps={steps} activeStep={activeStep} />
      <Box>{getStepContent()}</Box>
      <Box className="centralize buttons-container">
        <CancelButton
          id="invoice-stepper-back-button"
          onClick={() => setActiveStep(activeStep - 1)}
          disabled={activeStep === 0}
          text="Back"
        />
        <SaveButton
          id="invoice-stepper-save-next-button"
          onClick={handleNext}
          text={activeStep === steps.length - 1 ? 'Save' : 'Next'}
        />
      </Box>
      <Loader loading={createInvoiceResult.loading} />
    </DialogBox>
  );
};

export default InvoiceStepper;
